import { defineStore } from "pinia";
import { ref } from "vue";
import { Category, type User } from "@/model";
import { useAppStore } from "./app-store";
import CategoryDao from "./category-dao";
import { useUserStore } from "./user-store";

type Categories = {
  [domainId: string]: Category[];
};

interface Payload<T> {
  data: T; // カテゴリー
}
type PayloadAdd = Payload<Category>;
type PayloadAddAll = Payload<Category[]>;

function normalizeDomainId(category: Category): string | undefined;
function normalizeDomainId(category: Category, domainId: string): string;
function normalizeDomainId(
  category: Category,
  domainId?: string,
): string | undefined {
  if (category.domainId && Category.isDummy(category) === false)
    return category.domainId;
  return domainId;
}

export const useCategoryStore = defineStore("categories", () => {
  const categories = ref<Categories>({});
  const filterState = ref<{ [domainId: string]: Category[] }>({});

  const getDomainCategories = (domainId: string): Category[] => {
    if (!categories.value[domainId]) {
      categories.value = { ...categories.value, [domainId]: [] };
    }
    return categories.value[domainId];
  };

  function addOrUpdateCategory(category: Category) {
    const _domainId = normalizeDomainId(category);
    if (!_domainId) return;
    const domainCategories = getDomainCategories(_domainId);
    const index = domainCategories.findIndex((c) => c.id === category.id);
    if (index < 0) {
      domainCategories.push(category);
    } else {
      domainCategories.splice(index, 1, category);
    }
  }

  function sortTitle(category: Category) {
    const _domainId = normalizeDomainId(category);
    if (!_domainId) return;
    const domainCategories = getDomainCategories(_domainId);
    // タイトルでsort
    domainCategories.sort((p, n) => {
      if (p.title > n.title) return 1;
      else return -1;
    });
  }

  // --- getters ---
  const get = (domainId: string, exists = true): Category[] => {
    domainId = domainId || useAppStore().domainId;
    const result = categories.value[domainId];
    if (!result) return result;
    // ※ デフォルトで削除されていないカテゴリーのみ取得
    return exists ? result.filter((c) => Category.isExists(c)) : result;
  };
  const getOne = (
    domainId: string,
    id: string,
    _exists = false,
  ): Category | undefined => {
    domainId = domainId || useAppStore().domainId;
    const domainCategories = categories.value[domainId];
    return domainCategories
      ? domainCategories.find((c) => c.id === id)
      : undefined;
  };
  const getAll = (): Category[] => {
    return Object.keys(categories.value).reduce(
      (prev: Category[], domainId) => {
        const domainCategories = categories.value[domainId];
        prev.push(...domainCategories);
        return prev;
      },
      [],
    );
  };
  const filter = (domainId: string): Category[] => {
    if (!filterState.value[domainId]) {
      filterState.value = { ...filterState.value, [domainId]: [] };
    }
    return filterState.value[domainId];
  };

  // --- 内部更新 (旧 mutations 相当) ---
  const addMutation = (payload: PayloadAdd | PayloadAddAll): void => {
    if (Array.isArray(payload.data)) {
      (payload.data || []).forEach((category) => {
        addOrUpdateCategory(category);
        sortTitle(category);
      });
    } else {
      const category = payload.data;
      addOrUpdateCategory(category);
      sortTitle(category);
    }
  };

  const setFilterMutation = (payload: {
    domainId: string;
    filter: Category[];
  }): void => {
    const f = filterState.value[payload.domainId];
    if (f) {
      if (0 < f.length) f.splice(0, f.length);
      f.push(...payload.filter);
    } else {
      filterState.value = {
        ...filterState.value,
        [payload.domainId]: payload.filter,
      };
    }
  };

  const updateMutation = (payload: Category): void => {
    addOrUpdateCategory(payload);
    sortTitle(payload);
  };

  // --- actions ---
  const create = async (
    payload: Payload<Category>,
  ): Promise<Category | undefined> => {
    if (Category.isTemporary(payload.data) === false) return undefined;

    const category = payload.data;
    const register = await CategoryDao.create(category);
    if (register) {
      addMutation({ data: register });
    }
    // 注: 旧 Vuex 実装は register を返さず undefined を返していたため挙動を踏襲
    return undefined;
  };

  const add = async (payload: PayloadAdd): Promise<void> => {
    addMutation(payload);
  };

  const setFilter = async (payload: {
    domainId: string;
    filter: Category[];
  }): Promise<void> => {
    setFilterMutation(payload);
  };

  const fetch = async (payload: Payload<string>): Promise<void> => {
    const results = await CategoryDao.read(payload.data);
    if (results) {
      addMutation({ data: results });
    }
  };

  const update = async (payload: Partial<Category>): Promise<void> => {
    const category = getOne(payload.domainId as string, payload.id as string) as
      | Category
      | undefined;
    if (!category) {
      console.error("category is not found:", payload.domainId, payload.id);
      return;
    }
    const updated = category.copyFrom(payload);
    const register = await CategoryDao.update(updated);
    if (register) {
      updateMutation(register);
    }
  };

  const deleteCategory = async (payload: {
    domainId: string;
    id: string;
  }): Promise<void> => {
    // 実行ユーザーの取得
    const me = useUserStore().me as User;

    const category = getOne(payload.domainId, payload.id) as
      | Category
      | undefined;
    if (!category) {
      console.error("category is not found:", payload.domainId, payload.id);
      return;
    }
    const updated = category.copyFrom({
      deleted: true,
      deletedUser: me.id,
    });
    const register = await CategoryDao.update(updated); // 削除フラグを付けて更新
    if (register) {
      updateMutation(register);
    }
  };

  const registerSubscription = async (payload: {
    domainId: string;
    me: User;
  }): Promise<void> => {
    const domainId = payload.domainId;
    await CategoryDao.registerSubscrptions(domainId, {
      onCreate: (domainId: string) => {
        fetch({ data: domainId });
      },
      onUpdate: (domainId: string) => {
        fetch({ data: domainId });
      },
      onDelete: (domainId: string) => {
        updateMutation({ domainId } as unknown as Category);
      },
    });
  };

  const removeSubscription = async (payload: {
    domainId: string;
  }): Promise<void> => {
    await CategoryDao.closeSubscription(payload.domainId);
  };

  return {
    categories,
    filterState,
    get,
    getOne,
    getAll,
    filter,
    create,
    add,
    setFilter,
    fetch,
    update,
    delete: deleteCategory,
    registerSubscription,
    removeSubscription,
  };
});

export default useCategoryStore;
